import React, { useEffect, useMemo, useState } from 'react';
import { Select, Switch, Tooltip, Typography } from 'antd';
import { Button } from '../../../design-system';
import logger from '../../../utils/logger';
import { CHANNELS, FREQUENCY_LABELS } from './constants';

const { Text } = Typography;

const arraysEqual = (a, b) => {
    if (a.length !== b.length) return false;
    const sortedA = [...a].sort();
    const sortedB = [...b].sort();
    return sortedA.every((v, i) => v === sortedB[i]);
};

const isChannelAvailable = (availableChannels, channelKey) => {
    if (!availableChannels) return false;
    const info = availableChannels[channelKey];
    if (info === undefined) {
        // Missing entry is treated as unavailable so a backend regression that
        // omits the channel surfaces in the UI rather than silently passing.
        logger.warn(
            `[NotificationPreferences] channelAvailability missing entry for "${channelKey}"; treating as unavailable.`,
        );
        return false;
    }
    return info.available !== false;
};

const PreferenceControls = ({
    value,
    availableChannels,
    frequencies,
    onSave,
    saving = false,
}) => {
    const [channels, setChannels] = useState(value.channels);
    const [frequency, setFrequency] = useState(value.frequency);

    useEffect(() => {
        // NOTE: this overwrites unsaved local edits when the parent refetches
        // (e.g. after a "Send test" on G-Chat). Acceptable because the explicit
        // Save workflow means in-flight edits are non-persistent.
        setChannels(value.channels);
        setFrequency(value.frequency);
    }, [value.channels, value.frequency]);

    const dirty = useMemo(
        () => !arraysEqual(channels, value.channels) || frequency !== value.frequency,
        [channels, frequency, value.channels, value.frequency],
    );

    const isOff = frequency === 'off' || channels.length === 0;

    const frequencyOptions = useMemo(() => {
        if (!Array.isArray(frequencies) || frequencies.length === 0) {
            logger.warn(
                '[NotificationPreferences] frequencies missing/empty in API response; falling back to defaults.',
            );
            return Object.keys(FREQUENCY_LABELS).map((f) => ({
                value: f,
                label: FREQUENCY_LABELS[f] || f,
            }));
        }
        return frequencies.map((f) => ({ value: f, label: FREQUENCY_LABELS[f] || f }));
    }, [frequencies]);

    const toggleChannel = (channelKey, enabled) => {
        if (enabled) {
            setChannels((prev) => (prev.includes(channelKey) ? prev : [...prev, channelKey]));
        } else {
            setChannels((prev) => prev.filter((c) => c !== channelKey));
        }
    };

    const handleSave = () => {
        onSave({ channels, frequency });
    };

    return (
        <div className="notification-preferences__controls">
            <div className="notification-preferences__channels">
                {CHANNELS.map((channel) => {
                    const available = isChannelAvailable(availableChannels, channel.key);
                    const reason = availableChannels?.[channel.key]?.reason;
                    const switchEl = (
                        <Switch
                            checked={channels.includes(channel.key)}
                            onChange={(checked) => toggleChannel(channel.key, checked)}
                            disabled={!available}
                            aria-label={channel.label}
                        />
                    );
                    return (
                        <label
                            key={channel.key}
                            className="notification-preferences__channel"
                        >
                            {available ? (
                                switchEl
                            ) : (
                                <Tooltip title={reason ? `Unavailable: ${reason}` : 'Channel unavailable'}>
                                    <span>{switchEl}</span>
                                </Tooltip>
                            )}
                            <span>{channel.label}</span>
                        </label>
                    );
                })}
            </div>

            <div className="notification-preferences__frequency">
                <Text className="notification-preferences__frequency-label">Delivery:</Text>
                <Select
                    value={frequency}
                    onChange={setFrequency}
                    className="notification-preferences__frequency-select"
                    options={frequencyOptions}
                />
            </div>

            {isOff && (
                <div className="notification-preferences__off-hint">
                    <Text type="secondary">No notifications will be sent.</Text>
                </div>
            )}

            <div className="notification-preferences__save">
                <Button onClick={handleSave} disabled={!dirty || saving} loading={saving}>
                    Save
                </Button>
            </div>
        </div>
    );
};

export default PreferenceControls;
